import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Loader2, Calendar, MapPin, Download, Clock } from "lucide-react";
import { PortalHeader, PortalEmpty, PortalCard } from "@/components/portal/PortalHeader";
import { getMyProject, listMyAppointments, requestMyReschedule } from "@/lib/portal/portal.functions";

export const Route = createFileRoute("/_authenticated/portal/schedule")({
  component: SchedulePage,
});

const KIND_LABEL: Record<string, string> = {
  estimate: "Free estimate",
  color_consult: "Color consultation",
  start: "Project start",
  paint_day: "Paint day",
  walkthrough: "Final walkthrough",
  other: "Appointment",
};

const STATUS_COLORS: Record<string, string> = {
  scheduled: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  confirmed: "bg-emerald-400/10 text-emerald-300 border-emerald-400/20",
  reschedule_requested: "bg-amber-300/15 text-amber-200 border-amber-300/30",
  cancelled: "bg-red-500/10 text-red-300 border-red-500/20",
  completed: "bg-blue-400/10 text-blue-300 border-blue-400/20",
};

function toICSDate(iso: string) {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth()+1)}${pad(d.getUTCDate())}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}00Z`;
}

function downloadIcs(appt: { id: string; title: string; starts_at: string; ends_at: string | null; location: string | null; notes: string | null }) {
  const start = toICSDate(appt.starts_at);
  const end = toICSDate(appt.ends_at ?? new Date(new Date(appt.starts_at).getTime() + 60 * 60 * 1000).toISOString());
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Michaels Painting//Portal//EN",
    "BEGIN:VEVENT",
    `UID:${appt.id}@michaelspaintingwi.com`,
    `DTSTAMP:${toICSDate(new Date().toISOString())}`,
    `DTSTART:${start}`, `DTEND:${end}`,
    `SUMMARY:${appt.title.replace(/\n/g, " ")}`,
    appt.location ? `LOCATION:${appt.location.replace(/\n/g, " ")}` : "",
    appt.notes ? `DESCRIPTION:${appt.notes.replace(/\n/g, "\\n")}` : "",
    "END:VEVENT", "END:VCALENDAR",
  ].filter(Boolean).join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url;
  a.download = `${appt.title.replace(/[^a-z0-9]+/gi, "-")}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(url);
}

function SchedulePage() {
  const projectFn = useServerFn(getMyProject);
  const listFn = useServerFn(listMyAppointments);
  const projectQ = useQuery({ queryKey: ["portal", "project"], queryFn: () => projectFn() });
  const projectId = projectQ.data?.project?.id;
  const apptQ = useQuery({
    queryKey: ["portal", "appts", projectId],
    queryFn: () => listFn({ data: { projectId: projectId! } }),
    enabled: !!projectId,
  });

  if (projectQ.isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  if (!projectId) return <PortalEmpty title="No project yet" />;

  const appts = apptQ.data ?? [];
  const now = Date.now();
  const upcoming = appts.filter(a => new Date(a.starts_at).getTime() >= now);
  const past = appts.filter(a => new Date(a.starts_at).getTime() < now);

  return (
    <>
      <PortalHeader eyebrow="Schedule" title="Appointments" description="Estimate, color consult, project start, paint days, and final walkthrough — request a new time anytime." />
      {apptQ.isLoading ? (
        <div className="py-12 grid place-items-center"><Loader2 className="size-5 animate-spin text-amber-300" /></div>
      ) : appts.length === 0 ? (
        <PortalEmpty title="No appointments yet" description="Your project manager will schedule appointments as your project progresses." />
      ) : (
        <div className="space-y-8">
          {upcoming.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Upcoming</h2>
              {upcoming.map(a => <AppointmentRow key={a.id} appt={a} projectId={projectId} />)}
            </section>
          )}
          {past.length > 0 && (
            <section className="space-y-3">
              <h2 className="text-[11px] uppercase tracking-wider text-zinc-500 font-semibold">Past</h2>
              {past.map(a => <AppointmentRow key={a.id} appt={a} projectId={projectId} past />)}
            </section>
          )}
        </div>
      )}
    </>
  );
}

function AppointmentRow({ appt, past }: { appt: any; projectId: string; past?: boolean }) {
  const qc = useQueryClient();
  const requestFn = useServerFn(requestMyReschedule);
  const [open, setOpen] = useState(false);
  const [proposedAt, setProposedAt] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!proposedAt) return;
    setBusy(true);
    try {
      await requestFn({ data: { appointmentId: appt.id, proposedAt: new Date(proposedAt).toISOString(), note: note || undefined } });
      setOpen(false); setProposedAt(""); setNote("");
      await qc.invalidateQueries({ queryKey: ["portal", "appts"] });
    } catch (e: any) { alert(e?.message || "Could not request"); }
    finally { setBusy(false); }
  };

  let pendingProposal: { proposed_at?: string; note?: string } | null = null;
  if (appt.customer_response) {
    try { pendingProposal = JSON.parse(appt.customer_response); } catch {}
  }

  return (
    <PortalCard className={`p-4 sm:p-5 ${past ? "opacity-70" : ""}`}>
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div className="size-10 sm:size-11 rounded-xl bg-amber-300/10 border border-amber-300/20 grid place-items-center shrink-0">
            <Calendar className="size-4 sm:size-5 text-amber-300" />
          </div>
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-amber-300 font-semibold">{KIND_LABEL[appt.kind] ?? appt.kind}</p>
            <h3 className="text-white text-[15px] sm:text-base font-medium leading-snug break-words">{appt.title}</h3>
            <p className="text-xs text-zinc-400 mt-1.5 flex items-center gap-1.5 flex-wrap"><Clock className="size-3 shrink-0" />
              <span>{new Date(appt.starts_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
              {appt.ends_at && ` – ${new Date(appt.ends_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`}</span>
            </p>
            {appt.location && <p className="text-xs text-zinc-500 mt-1 flex items-start gap-1.5"><MapPin className="size-3 shrink-0 mt-0.5" /><span className="break-words">{appt.location}</span></p>}
            {appt.notes && <p className="text-xs text-zinc-400 mt-2 leading-relaxed">{appt.notes}</p>}
          </div>
        </div>
        <span className={`text-[9px] sm:text-[10px] uppercase tracking-wider px-2 sm:px-2.5 py-1 rounded-full border shrink-0 whitespace-nowrap ${STATUS_COLORS[appt.status] ?? STATUS_COLORS.scheduled}`}>
          {appt.status.replace("_", " ")}
        </span>
      </div>

      {pendingProposal?.proposed_at && (
        <div className="mt-4 rounded-xl bg-amber-300/5 border border-amber-300/20 p-3 text-xs">
          <p className="text-amber-200">Reschedule requested for <strong>{new Date(pendingProposal.proposed_at).toLocaleString()}</strong></p>
          {pendingProposal.note && <p className="text-zinc-400 mt-1">"{pendingProposal.note}"</p>}
          <p className="text-zinc-500 mt-1">Awaiting team confirmation.</p>
        </div>
      )}

      {!past && (
        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => downloadIcs(appt)} className="inline-flex items-center justify-center gap-1.5 text-xs text-zinc-300 hover:text-amber-300 border border-white/10 bg-white/5 hover:bg-white/10 rounded-full px-3.5 py-2 min-h-9">
            <Download className="size-3.5" /> Add to calendar
          </button>
          {appt.status !== "reschedule_requested" && (
            <button onClick={() => setOpen(!open)} className="inline-flex items-center justify-center gap-1.5 text-xs text-zinc-200 border border-white/10 bg-white/5 hover:bg-white/10 rounded-full px-3.5 py-2 min-h-9">
              Request reschedule
            </button>
          )}
        </div>
      )}

      {open && (
        <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-3.5 sm:p-4 space-y-3">
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">New date & time</label>
            <input type="datetime-local" value={proposedAt} onChange={(e) => setProposedAt(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2.5 text-base sm:text-sm text-white min-h-11" />
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Note (optional)</label>
            <textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-base sm:text-sm text-white resize-none" />
          </div>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setOpen(false)} className="text-xs text-zinc-400 px-3 py-2 min-h-9">Cancel</button>
            <button onClick={submit} disabled={!proposedAt || busy}
              className="bg-amber-300 text-zinc-950 text-xs font-semibold px-4 py-2.5 min-h-10 rounded-full inline-flex items-center gap-1.5 disabled:opacity-50">
              {busy && <Loader2 className="size-3 animate-spin" />} Send request
            </button>
          </div>
        </div>
      )}
    </PortalCard>
  );
}